// RFC 4918 §10.4 If header — full production parsing and evaluation
// (browserdav-nephele-parity Phase 3, decision 3).
//
//   If = ( 1*No-tag-list | 1*Tagged-list )
//   Tagged-list = "<" resource URL ">" 1*List
//   List = "(" 1*( ["Not"] ( "<" state-token ">" | "[" entity-tag "]" ) ) ")"
//
// Semantics: within a list, conditions AND; across lists (for the same
// resource), OR. The request fails with 412 unless some list evaluates true.
// State tokens submitted anywhere in the header also serve as lock-token
// submission for the lock check (the common liberal reading).

/**
 * Parse an If header string into
 *   [{ resourceTag: string|null, conditions: [{ not, token?, etag? }] }]
 * Throws on grammar it cannot recognize (caller maps to 400).
 */
export function parseIfHeader(headerValue) {
    const lists = []
    let currentTag = null
    let rest = String(headerValue).trim()

    while (rest.length > 0) {
        if (rest.startsWith('<')) {
            // Resource-Tag preceding its lists
            const end = rest.indexOf('>')
            if (end === -1) throw new Error('Unterminated resource tag in If header')
            currentTag = rest.slice(1, end)
            rest = rest.slice(end + 1).trim()
        } else if (rest.startsWith('(')) {
            const end = rest.indexOf(')')
            if (end === -1) throw new Error('Unterminated condition list in If header')
            const inner = rest.slice(1, end).trim()
            rest = rest.slice(end + 1).trim()

            const conditions = []
            let chunk = inner
            while (chunk.length > 0) {
                let not = false
                if (/^not\s/i.test(chunk)) {
                    not = true
                    chunk = chunk.slice(4).trim()
                }
                if (chunk.startsWith('<')) {
                    const tokenEnd = chunk.indexOf('>')
                    if (tokenEnd === -1) throw new Error('Unterminated state token in If header')
                    conditions.push({ not, token: chunk.slice(1, tokenEnd) })
                    chunk = chunk.slice(tokenEnd + 1).trim()
                } else if (chunk.startsWith('[')) {
                    const etagEnd = chunk.indexOf(']')
                    if (etagEnd === -1) throw new Error('Unterminated entity tag in If header')
                    conditions.push({
                        not,
                        etag: chunk.slice(1, etagEnd).replace(/^W\//, '').replace(/^"|"$/g, ''),
                    })
                    chunk = chunk.slice(etagEnd + 1).trim()
                } else {
                    throw new Error(`Unrecognized If header condition near: ${chunk.slice(0, 20)}`)
                }
            }
            if (conditions.length === 0) throw new Error('Empty condition list in If header')
            lists.push({ resourceTag: currentTag, conditions })
        } else {
            throw new Error(`Unrecognized If header syntax near: ${rest.slice(0, 20)}`)
        }
    }

    if (lists.length === 0) throw new Error('If header contains no condition lists')
    return lists
}

/**
 * Every state token submitted anywhere in the header (used for lock-token
 * submission regardless of which list carried it).
 */
export function submittedTokens(parsedLists) {
    const tokens = new Set()
    for (const list of parsedLists) {
        for (const condition of list.conditions) {
            if (condition.token) tokens.add(condition.token)
        }
    }
    return tokens
}

/**
 * Evaluate the parsed header. resolve(resourceTag|null) must return
 * { etag: string|null, lockTokens: Set<string> } for the list's target
 * resource (null tag = the request-URI resource). Async to allow validator
 * resolution. Returns true when some list holds.
 */
export async function evaluateIfHeader(parsedLists, resolve) {
    for (const list of parsedLists) {
        const target = await resolve(list.resourceTag)
        if (!target) continue // unresolvable tagged resource → list cannot hold
        let holds = true
        for (const condition of list.conditions) {
            let value
            if (condition.token !== undefined) {
                value = target.lockTokens.has(condition.token)
            } else {
                value = target.etag != null && target.etag === condition.etag
            }
            if (condition.not) value = !value
            if (!value) {
                holds = false
                break
            }
        }
        if (holds) return true
    }
    return false
}
